// Google Analytics 4 + Microsoft Clarity — the one place that loads them,
// tracks consent, and sends screen views / events.
//
// Consent (UK users need it for analytics cookies; Clarity enforces it for
// UK/EEA traffic since 31 Oct 2025):
//   - undecided: both tools run in their vendor "no consent" modes — GA sends
//     cookieless pings, Clarity uses no cookies and a new ID per page load.
//   - accepted:  full tracking with cookies, linked to our internal user ID.
//   - declined:  GA stays in denied mode; Clarity is told to stop tracking.
// The user ID is never attached unless consent is granted.
//
// The remote scripts load only in production builds (web + APK). In dev and
// in tests every call still goes into window.dataLayer / window.clarity.q,
// which is what the e2e suite inspects — and nothing pollutes real data.
//
// Never send PII (email, name, typed text) through here. Google and Microsoft
// both prohibit it; the internal user UUID is the only identifier allowed.

import { useEffect, useRef } from 'react';

const GA_ID = 'G-N0597BPTPG';
const CLARITY_ID = 'wparznp3bf';
const CONSENT_KEY = 'pm-analytics-consent';
const OPEN_CHOICES_EVENT = 'pm-open-consent-choices';
const CONSENT_CHANGE_EVENT = 'pm-consent-change';

export type Consent = 'granted' | 'denied';
type Params = Record<string, string | number | boolean>;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    clarity: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

let userId: string | null = null;
let userTags: Record<string, string> = {};
let lastScreen: string | null = null;

// Analytics must never be able to break the app (ad blockers, privacy modes).
function safe(fn: () => void): void {
  try { fn(); } catch { /* ignore */ }
}

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'granted' || v === 'denied' ? v : null;
  } catch {
    return null;
  }
}

function clarityGrant(): void {
  safe(() => window.clarity('consentv2', { ad_Storage: 'denied', analytics_Storage: 'granted' }));
}

function clarityStop(): void {
  // Documented by Microsoft: clears Clarity cookies and stops tracking until
  // consent is granted again.
  safe(() => window.clarity('consent', false));
}

/** Call once, before React renders. */
export function initAnalytics(): void {
  // Captured now, before anything renders: an unauthenticated landing on
  // /?utm_source=… is immediately redirected to /auth, which drops the query
  // string. Passing it explicitly keeps the source attribution.
  const landingUrl = window.location.href;

  window.dataLayer = window.dataLayer || [];
  // Must push the `arguments` object itself, not an array — gtag.js relies on it.
  window.gtag = window.gtag || function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.clarity = window.clarity || (function clarity() {
    // eslint-disable-next-line prefer-rest-params
    (window.clarity.q = window.clarity.q || []).push(arguments);
  } as Window['clarity']);

  const consent = getConsent();
  safe(() => window.gtag('consent', 'default', {
    analytics_storage: consent === 'granted' ? 'granted' : 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  }));
  safe(() => window.gtag('js', new Date()));
  safe(() => window.gtag('config', GA_ID, { page_location: landingUrl }));

  if (consent === 'granted') clarityGrant();
  else if (consent === 'denied') clarityStop();

  if (import.meta.env.PROD) {
    for (const src of [`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`, `https://www.clarity.ms/tag/${CLARITY_ID}`]) {
      const s = document.createElement('script');
      s.async = true;
      s.src = src;
      document.head.appendChild(s);
    }
  }
}

export function setConsent(choice: Consent): void {
  try { localStorage.setItem(CONSENT_KEY, choice); } catch { /* storage blocked */ }
  safe(() => window.gtag('consent', 'update', { analytics_storage: choice }));
  if (choice === 'granted') {
    clarityGrant();
    applyIdentity();
  } else {
    safe(() => window.gtag('set', { user_id: null }));
    clarityStop();
  }
  window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
}

export function onConsentChange(handler: () => void): () => void {
  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

/** Re-open the consent banner (from the You tab → Privacy choices). */
export function openConsentChoices(): void {
  window.dispatchEvent(new Event(OPEN_CHOICES_EVENT));
}

export function onOpenConsentChoices(handler: () => void): () => void {
  window.addEventListener(OPEN_CHOICES_EVENT, handler);
  return () => window.removeEventListener(OPEN_CHOICES_EVENT, handler);
}

function applyIdentity(): void {
  if (!userId || getConsent() !== 'granted') return;
  const id = userId;
  safe(() => window.gtag('set', { user_id: id }));
  safe(() => window.clarity('identify', id));
  for (const [key, value] of Object.entries(userTags)) safe(() => window.clarity('set', key, value));
}

/** Internal user UUID only — never an email. Applied only with consent. */
export function setUser(id: string | null, tags: Record<string, string> = {}): void {
  userId = id;
  userTags = tags;
  applyIdentity();
}

/**
 * A screen became visible. The app keeps one URL for everything, so these
 * virtual page views are what let GA show time-per-screen and Clarity filter
 * recordings by screen. Consecutive duplicates are ignored (also absorbs
 * React StrictMode's double effects in dev).
 */
export function trackScreen(name: string): void {
  if (name === lastScreen) return;
  lastScreen = name;
  safe(() => window.gtag('event', 'page_view', {
    page_title: name,
    page_location: `${window.location.origin}/screen/${name}`,
  }));
  safe(() => window.clarity('set', 'screen', name));
  safe(() => window.clarity('event', `screen_${name.replace(/[^a-z0-9]+/gi, '_')}`));
}

/** `beacon: true` for events fired right before navigating away (e.g. to Cognito). */
export function track(event: string, params: Params = {}, opts: { beacon?: boolean } = {}): void {
  safe(() => window.gtag('event', event, opts.beacon ? { ...params, transport_type: 'beacon' } : params));
  safe(() => window.clarity('event', event));
}

/**
 * Declares the screen a component is showing. Pass null when nothing is open
 * (e.g. an overlay is closed); `returnTo` is re-tracked when it closes, so the
 * time after closing a sheet isn't credited to the sheet.
 */
export function useScreenView(name: string | null, returnTo?: string): void {
  const wasOpen = useRef(false);
  useEffect(() => {
    if (name) {
      wasOpen.current = true;
      trackScreen(name);
    } else if (wasOpen.current) {
      wasOpen.current = false;
      if (returnTo) trackScreen(returnTo);
    }
  }, [name]); // eslint-disable-line react-hooks/exhaustive-deps
}
